import type { Tables } from '../../types/database.types';

export type Profile = Tables<'profiles'>;

export type DenialReason =
  | 'invite_expired'
  | 'invite_revoked'
  | 'no_valid_invite'
  | 'inactive_profile'
  | 'not_admin'
  | 'unknown_error';

export type AccessState =
  | { status: 'loading' }
  | { status: 'signed_out' }
  | { status: 'denied'; reason: DenialReason }
  | { status: 'approved'; profile: Profile };

const KNOWN_DENIALS: ReadonlySet<string> = new Set([
  'invite_expired',
  'invite_revoked',
  'no_valid_invite',
]);

/**
 * Maps an accept_invite database error message to a denial reason.
 * Unknown errors deny access rather than failing open.
 */
export function denialFromInviteError(message: string): DenialReason {
  return KNOWN_DENIALS.has(message) ? (message as DenialReason) : 'unknown_error';
}

/**
 * Resolves the access state for an authenticated user from their profile
 * lookup result. Authentication proves identity only (contract 4.6); access
 * requires an approved, active profile.
 */
export function resolveAccess(profile: Profile | null): AccessState {
  if (!profile) {
    return { status: 'denied', reason: 'no_valid_invite' };
  }
  if (!profile.is_active) {
    return { status: 'denied', reason: 'inactive_profile' };
  }
  return { status: 'approved', profile };
}

export const DENIAL_MESSAGES: Record<DenialReason, string> = {
  invite_expired: 'Your invitation has expired. Ask a TAP admin to send a new one.',
  invite_revoked:
    'Your invitation was revoked. Contact a TAP admin if you believe this is a mistake.',
  no_valid_invite: 'This Google account has no access. Ask a TAP admin for an invitation.',
  inactive_profile: 'Your account is deactivated. Contact a TAP admin to restore access.',
  not_admin: 'This area is limited to TAP admins.',
  unknown_error:
    'Something went wrong while checking your access. Try again or contact a TAP admin.',
};
